import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Eye, Copy, Check } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SignatureRevealDialog({ open, onOpenChange, card }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setPassword("");
    setResult(null);
    setCopied(false);
  };

  const handleReveal = async () => {
    if (!password) return;
    setLoading(true);
    try {
      const res = await api.post(`/cards/${card.id}/reveal`, { password });
      setResult(res.data);
      toast.success("Signature unlocked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const copySig = () => {
    navigator.clipboard.writeText(result.full_signature);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const fmt = (s) => s.match(/.{1,4}/g)?.join(" ") || s;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#00F0FF]" /> Reveal Full Signature
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Enter your card password to unlock the 16 sealed confirmation digits.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 pt-2">
            <Input
              data-testid="reveal-password-input"
              type="password"
              placeholder="Card password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleReveal()}
              className="bg-[#1A1A20] border-white/10 text-white h-12 font-mono-sig"
            />
            <Button
              data-testid="reveal-submit-button"
              onClick={handleReveal}
              disabled={loading || !password}
              className="w-full h-12 bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold rounded-xl"
            >
              <Eye className="w-4 h-4 mr-2" /> {loading ? "Unlocking…" : "Unlock Signature"}
            </Button>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 pt-2"
          >
            <div className="rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4">
              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#A0A0AB] mb-2">
                Public 16 digits
              </p>
              <p className="font-mono-sig text-lg tracking-[0.15em] text-white break-all">
                {fmt(result.signature_public)}
              </p>
            </div>
            <div className="rounded-xl border border-[#FF007F]/30 bg-[#FF007F]/5 p-4">
              <p className="font-mono-sig text-[9px] tracking-[0.3em] uppercase text-[#FF007F] mb-2">
                Sealed 16 digits · Confirmation
              </p>
              <p data-testid="revealed-secret" className="font-mono-sig text-lg tracking-[0.15em] text-white break-all">
                {fmt(result.signature_secret)}
              </p>
            </div>
            <Button
              data-testid="copy-signature-button"
              onClick={copySig}
              variant="outline"
              className="w-full h-11 border-white/10 bg-white/5 text-white hover:bg-white/10 rounded-xl"
            >
              {copied ? <Check className="w-4 h-4 mr-2 text-[#00FF9D]" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? "Copied" : "Copy full 32-digit signature"}
            </Button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
